import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CasingSearchRequest } from '../model/casing-search-request.model';
import { Store } from '@ngxs/store';
import { DealerState } from '../../../shared/state/DealerState';
import { CasingSearchApiResponse, PaginatedCasingSearchResponse } from '../model/casing-search-response.model';
import { DropdownData } from '../model/casing-auto-complete.model';
import { TransferCasingExistingWoRequest, TransferCasingExistingWoResponse } from '../model/transfer-casing-existing-wo.model';

export interface TransferCasingsResponse {
  data?: {
    message?: string;
    casingIds?: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class CasingSearchService {
  public http = inject(HttpClient);
  public store = inject(Store);

  /**
   * In-memory (non-persisted) flag indicating a live search was just performed in this
   * browser session and the results page can safely reuse the results already dispatched
   * to the store instead of re-fetching page 1 immediately.
   *
   * This intentionally does NOT survive a full page reload (unlike the NGXS "casingSearch"
   * state, which is persisted to localStorage). That guarantees a hard refresh always
   * triggers a fresh network fetch instead of showing stale/cached results (e.g. rows that
   * were bulk deleted before the refresh).
   */
  public justSearched = false;

  constructor() {}

  // Main search method using new API structure
  searchCasings(request: CasingSearchRequest): Observable<PaginatedCasingSearchResponse> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }
    const url = environment.backOfficeUrl + environment.serviceUris.casingSearch.replace('{dealerId}', currentDealer.id);

    return this.http.post<CasingSearchApiResponse>(url, request).pipe(
      map((response) => {
        if (response.error && response.error.length > 0) {
          throw new Error(response.error.join(', '));
        }
        return response.data;
      })
    );
  }

  retrieveCasingsMake(): Observable<DropdownData> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }
    const url = environment.backOfficeUrl + environment.serviceUris.getMakesForDealer.replace('{dealerId}', currentDealer.id);

    return this.http.get<DropdownData>(url).pipe(
      map((response) => {
        return response;
      })
    );
  }

  retrieveCasingtreadWidths(): Observable<DropdownData> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }
    const url = environment.backOfficeUrl + environment.serviceUris.getTreadWidthsForDealer.replace('{dealerId}', currentDealer.id);

    return this.http.get<DropdownData>(url).pipe(
      map((response) => {
        return response;
      })
    );
  }

  retrieveCasingSeries(): Observable<DropdownData> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }
    const url = environment.backOfficeUrl + environment.serviceUris.getCasingSeriesForDealer.replace('{dealerId}', currentDealer.id);

    return this.http.get<DropdownData>(url).pipe(
      map((response) => {
        return response;
      })
    );
  }

  retrieveCasingSizes(): Observable<DropdownData> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }
    const url = environment.backOfficeUrl + environment.serviceUris.getCasingSizesForDealer.replace('{dealerId}', currentDealer.id);

    return this.http.get<DropdownData>(url).pipe(
      map((response) => {
        return response;
      })
    );
  }

  retrieveCasingTreads(): Observable<DropdownData> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }
    const url = environment.backOfficeUrl + environment.serviceUris.getTreadsForDealer.replace('{dealerId}', currentDealer.id);

    return this.http.get<DropdownData>(url).pipe(
      map((response) => {
        return response;
      })
    );
  }

  transferCasings(casingIds: string[]): Observable<TransferCasingsResponse> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }

    const url = environment.backOfficeUrl + environment.serviceUris.checkTransferCasings.replace('{dealerId}', currentDealer.id);
    const params = new HttpParams().set('casingIds', casingIds.join(','));

    return this.http.post<TransferCasingsResponse>(url, { params });
  }

  transferCasingToExistingWo(casingIds: string[], targetWohId: string): Observable<TransferCasingExistingWoResponse> {
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (!currentDealer?.id) {
      throw new Error('No dealer selected');
    }

    const request: TransferCasingExistingWoRequest = {
      targetWorkOrderId: targetWohId,
      casingIds: casingIds
    };
    const url = environment.backOfficeUrl + environment.serviceUris.transferToExistingWo.replace('{dealerId}', currentDealer.id);
    return this.http.post<TransferCasingExistingWoResponse>(url, request);
  }
}
